/** @jsxImportSource @emotion/react */

import { Trans, useLingui } from '@lingui/react/macro';
import classNames from 'classnames';
import { switchUserTournament } from 'modules/auth/store/actions';
import AuthLayout from 'modules/auth/ui/layouts';
import { useAuthGuard } from 'modules/auth/ui/hooks/use-auth-guard';
import { useAvailableOrganizers } from 'modules/auth/ui/hooks/use-available-organizers';
import { Pages } from 'modules/navigation/domain/enums/pages';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { scrollbar } from 'shared/styles/scrollbar';

import { AppDispatch } from 'store/store';
import { theme } from 'styles/theme/theme';
import { UDText } from 'ud-ui/components';
import UDAsyncContent from 'ud-ui/components/async-content';
import UDButton from 'ud-ui/components/button';
import Pagination from 'ud-ui/components/pagination';
import UDSearchInput from 'ud-ui/components/search';

/** Высота области выдачи. Задана явно, чтобы спиннер и список не двигали вёрстку. */
const BODY_MIN_HEIGHT = 320;

const AuthPagesSelectTournament = () => {
  useAuthGuard();

  const { t } = useLingui();
  const dispatch = useDispatch<AppDispatch>();
  const { state, isError, data: availableTournaments, hasSearch, pages, page, search, setSearch, setPage } = useAvailableOrganizers();

  const handleTournamentSelected = useCallback(
    (tournamentId: number) => async () => {
      dispatch(switchUserTournament(tournamentId));
    },
    [dispatch],
  );

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (availableTournaments.length > 0) {
          void handleTournamentSelected(availableTournaments[0].id)();
        }
      }
    },
    [availableTournaments, handleTournamentSelected],
  );

  useEffect(() => {
    if (isError) {
      toast.error(t`Произошла ошибка при получении турниров, перезагрузите страницу`);
    }
  }, [isError, t]);

  const message = useCallback(
    (text: string) => (
      <UDText variant="text-2" className="text-WhiteGray text-center">
        {text}
      </UDText>
    ),
    [],
  );

  const emptyMessage = useMemo(
    // Пустой поиск и пустой список — разные вещи: во втором случае у пользователя
    // просто нет организаций, и он ничего не искал.
    () => message(hasSearch ? t`Организатора с таким названием нет.` : t`У вас пока нет доступных организаторов.`),
    [hasSearch, message, t],
  );

  return (
    <AuthLayout
      title=""
      subtitle=""
      logoTo={Pages.INDEX}
      contentClassName="overflow-hidden min-h-0"
      formContainerProps={{
        className: 'flex flex-col h-full min-h-0',
        style: {
          height: 522,
          justifyContent: 'flex-start',
        },
      }}
    >
      <div className="flex h-full min-h-0 flex-col">
        <UDText as="h2" variant="title-2" color="white" className="mb-5">
          <Trans>Список организаторов</Trans>
        </UDText>
        <UDSearchInput
          autoFocus
          placeholder={t`Название организации`}
          value={search}
          loading={state.isRefreshing}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={handleSearchKeyDown}
          className="mb-7.5"
        />
        <UDAsyncContent
          state={state}
          minHeight={BODY_MIN_HEIGHT}
          error={message(t`Произошла ошибка, попробуйте ещё раз`)}
          empty={emptyMessage}
          className="text-surfaceWhite flex min-h-0 flex-1 flex-col"
          data-testid="organizers-list"
        >
          <div css={scrollbar(theme)} className="min-h-0 flex-1 overflow-y-auto">
            {availableTournaments.map((tournament, index) => (
              <div className={classNames({ 'mt-2.5': index !== 0 })} key={tournament.id}>
                <UDButton className="w-full" variant="primary" type="button" onClick={handleTournamentSelected(tournament.id)}>
                  {tournament.name}
                </UDButton>
              </div>
            ))}
            <div className="flex-center w-full">
              <Pagination pages={pages} currentPage={page} onPageChange={setPage} pagesShow={8} className="mt-7.5" />
            </div>
          </div>
        </UDAsyncContent>
      </div>
    </AuthLayout>
  );
};

export default AuthPagesSelectTournament;
